<template>
    <div class="mt-4">
        <VerificationSubsection
            :helper-text="IDV_HELPER_ID_READABILITY"
            title="ID Readability"
        >
            <VerificationFullRow
                :check-result="govIdAnalysis.isIdReadable"
                label="Is the ID readable?"
                omit-check-text
            />
            <VerificationFullRow
                :check-result="govIdAnalysis.isBarcodeReadable"
                label="Is the barcode readable?"
                omit-check-text
            />
        </VerificationSubsection>
        <VerificationSubsection
            :helper-text="IDV_HELPER_ID_AUTHENTICITY"
            title="ID Authenticity"
        >
            <VerificationFullRow
                :check-result="govIdAnalysis.isIdAuthentic"
                label="Is the ID authentic?"
                omit-check-text
            />
            <div
                v-if="govIdAnalysis.docFrontUrl || govIdAnalysis.docBackUrl"
                class="flex w-full flex-row justify-items-center gap-3 px-4 py-3 font-medium text-ds-primary-400"
            >
                <div
                    v-if="govIdAnalysis.docFrontUrl"
                    class="flex max-w-[50%] flex-1 flex-col gap-1"
                >
                    FRONT
                    <img :src="govIdAnalysis.docFrontUrl" />
                </div>
                <div
                    v-if="govIdAnalysis.docBackUrl"
                    class="flex max-w-[50%] flex-1 flex-col gap-1"
                >
                    BACK
                    <img :src="govIdAnalysis.docBackUrl" />
                </div>
            </div>
        </VerificationSubsection>
        <VerificationSubsection
            v-if="country === 'US'"
            :helper-text="IDV_HELPER_DMV_VERIFICATION"
            title="DMV Verification"
        >
            <CheckErrorContainer v-if="govIdAnalysis.aamvaDisabledState">
                <p>
                    The state of {{ govIdAnalysis.issuingAuthority }} does not participate with the American Association of Motor
                    Vehicle Administrators (AAMVA) to verify DMV records.
                </p>
            </CheckErrorContainer>

            <template v-if="govIdAnalysis.isAamvaEligibleId">
                <VerificationFullRow
                    :check-result="govIdAnalysis.idNumber"
                    :indentation-level="2"
                    label="ID Number:"
                />
                <VerificationFullRow
                    :check-result="govIdAnalysis.fullName"
                    :indentation-level="2"
                    label="Full Name:"
                />
                <VerificationFullRow
                    :check-result="govIdAnalysis.currentAddress"
                    :indentation-level="2"
                    label="Postal Code:"
                />
                <VerificationFullRow
                    :check-result="govIdAnalysis.dateOfBirth"
                    :indentation-level="2"
                    label="Date of Birth:"
                />
                <VerificationFullRow
                    :check-result="govIdAnalysis.expiryDate"
                    :indentation-level="2"
                    label="Expiry Date:"
                />
                <VerificationFullRow
                    :check-result="govIdAnalysis.issueDate"
                    :indentation-level="2"
                    label="Issue Date:"
                />
            </template>
            <CheckErrorContainer v-else>
                <p>
                    The Department of Motor Vehicles (DMV) verification could not be performed because the ID provided was not a
                    driver's license.
                </p>
            </CheckErrorContainer>
        </VerificationSubsection>
    </div>
</template>

<script lang="ts" setup>
import type { GovIDAnalysis } from '@/api/interfaces/identity-verification-report.api';
import CheckErrorContainer from '../common/CheckErrorContainer.vue';
import VerificationFullRow from '../common/VerificationFullRow.vue';
import VerificationSubsection from '../common/VerificationSubsection.vue';
import { IDV_HELPER_DMV_VERIFICATION, IDV_HELPER_ID_AUTHENTICITY, IDV_HELPER_ID_READABILITY } from '../idv-static-helper-text';

defineProps<{ govIdAnalysis: GovIDAnalysis; country: string }>();
</script>
